import { SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useState } from 'react';
import {
	Box,
	Checkbox,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from 'styles';
import palette from 'styles/theme/Palette';
import { ISelectData } from 'types/Billing';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';

interface IMultipleSelect {
	sx?: SxProps<Theme>;
	name: string;
	items: ISelectData[];
	value: string[];
	onChange: (e: string[]) => void;
	withAll?: boolean;
}

const MultipleSelect = ({
	items,
	name,
	sx,
	value,
	onChange,
	withAll = true,
}: IMultipleSelect) => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});

	const [open, setOpen] = useState<boolean>(false);

	const handleOnChange = (selectedValue: string) => {
		const isAlreadySelected = value.includes(selectedValue);

		if (isAlreadySelected) {
			const newValue = value.filter(v => v !== selectedValue);
			onChange(newValue.length > 0 ? newValue : ['All']);
		} else {
			const newValue = value.filter(v => v !== 'All');
			onChange([...newValue, selectedValue]);
		}
	};

	const handleOnChangeAll = () => {
		onChange(['All']);
		setOpen(false);
	};

	return (
		<FormControl sx={{ flex: 1, width: '100%', maxWidth: '300px' }}>
			<InputLabel>{tClientLabels(`commons.filtersHeader.${name}`)}</InputLabel>
			<Select
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				sx={{ ...sx, height: '56px', width: '100%' }}
				multiple
				variant="filled"
				name={tClientLabels(`commons.filtersHeader.${name}`) as string}
				value={value}
				native={false}
				displayEmpty={true}
				fullWidth
				renderValue={(e: any) => {
					const newData = e.map(
						(el: string) => items.find(item => item.code === el)?.name,
					);
					return newData.join(', ') || tClientLabels('forms.status.All');
				}}
				MenuProps={{
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'left',
					},
					transformOrigin: {
						vertical: 'top',
						horizontal: 'left',
					},
					PaperProps: {
						sx: {
							maxHeight: '268px',
							'& li': {
								padding: '0 7px',
							},
						},
					},
				}}
				disableUnderline
			>
				{withAll && (
					<>
						<MenuItem
							key={'all'}
							value={'All'}
							disableRipple
							onClick={handleOnChangeAll}
						>
							<Checkbox
								label={tClientLabels('forms.status.All')}
								checked={value.includes('All')}
							/>
						</MenuItem>
						<Box
							sx={{
								width: 'calc(100% - 32px)',
								margin: '0 16px',
								height: '1px',
								background: palette.grey4.main,
							}}
						/>
					</>
				)}
				{items.map(item => {
					return (
						<MenuItem
							key={item.code}
							value={item.name}
							disableRipple
							onClick={e => {
								e.preventDefault();
								handleOnChange(item.code);
							}}
						>
							<Checkbox label={item.name} checked={value.includes(item.code)} />
						</MenuItem>
					);
				})}
			</Select>
		</FormControl>
	);
};

export default MultipleSelect;
